import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CentroCostoContable, AreaContable } from '../types/centroCosto';
import { CentroCostoService } from '../services/centroCostoService';

interface CentroCostoState {
  centrosCosto: CentroCostoContable[];
  areas: AreaContable[];
  loading: boolean;
  error: string | null;
  dialogOpen: boolean;
  selectedCentroCosto: CentroCostoContable | null;

  fetchCentrosCosto: () => Promise<void>;
  saveCentroCosto: (centroCosto: Partial<CentroCostoContable>) => Promise<void>;
  deleteCentroCosto: (id: number) => Promise<void>;
  fetchAreas: () => Promise<void>;
  saveArea: (area: Partial<AreaContable>) => Promise<void>;
  deleteArea: (id: number) => Promise<void>;
  setSelectedCentroCosto: (centroCosto: CentroCostoContable | null) => void;
  openDialog: () => void;
  closeDialog: () => void;
  clearError: () => void;
}

export const useCentroCostoStore = create<CentroCostoState>()(
  devtools(
    (set) => ({
      centrosCosto: [],
      areas: [],
      loading: false,
      error: null,
      dialogOpen: false,
      selectedCentroCosto: null,

      fetchCentrosCosto: async () => {
        set({ loading: true, error: null }, false, 'centroCosto/fetch/start');
        try {
          const centrosCosto = await CentroCostoService.getAll();
          set({ centrosCosto, loading: false }, false, 'centroCosto/fetch/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar centros de costo';
          set({ loading: false, error: errorMessage }, false, 'centroCosto/fetch/error');
        }
      },

      saveCentroCosto: async (centroCosto) => {
        set({ loading: true, error: null }, false, 'centroCosto/save/start');
        try {
          await CentroCostoService.save(centroCosto);
          const centrosCostoList = await CentroCostoService.getAll();
          set({ centrosCosto: centrosCostoList, loading: false, dialogOpen: false, selectedCentroCosto: null }, false, 'centroCosto/save/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al guardar centro de costo';
          set({ loading: false, error: errorMessage }, false, 'centroCosto/save/error');
          throw error;
        }
      },

      deleteCentroCosto: async (id) => {
        set({ loading: true, error: null }, false, 'centroCosto/delete/start');
        try {
          await CentroCostoService.delete(id);
          const centrosCosto = await CentroCostoService.getAll();
          set({ centrosCosto, loading: false }, false, 'centroCosto/delete/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al eliminar centro de costo';
          set({ loading: false, error: errorMessage }, false, 'centroCosto/delete/error');
          throw error;
        }
      },

      fetchAreas: async () => {
        set({ loading: true, error: null }, false, 'centroCosto/fetchAreas/start');
        try {
          const areas = await CentroCostoService.getAllAreas();
          set({ areas, loading: false }, false, 'centroCosto/fetchAreas/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al cargar areas';
          set({ loading: false, error: errorMessage }, false, 'centroCosto/fetchAreas/error');
        }
      },

      saveArea: async (area) => {
        set({ loading: true, error: null }, false, 'centroCosto/saveArea/start');
        try {
          await CentroCostoService.saveArea(area);
          const areas = await CentroCostoService.getAllAreas();
          set({ areas, loading: false }, false, 'centroCosto/saveArea/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al guardar area';
          set({ loading: false, error: errorMessage }, false, 'centroCosto/saveArea/error');
          throw error;
        }
      },

      deleteArea: async (id) => {
        set({ loading: true, error: null }, false, 'centroCosto/deleteArea/start');
        try {
          await CentroCostoService.deleteArea(id);
          const areas = await CentroCostoService.getAllAreas();
          set({ areas, loading: false }, false, 'centroCosto/deleteArea/success');
        } catch (error: unknown) {
          const errorMessage = (error as Record<string, unknown>)?.message as string || 'Error al eliminar area';
          set({ loading: false, error: errorMessage }, false, 'centroCosto/deleteArea/error');
          throw error;
        }
      },

      setSelectedCentroCosto: (centroCosto) => set({ selectedCentroCosto: centroCosto }, false, 'centroCosto/setSelected'),
      openDialog: () => set({ dialogOpen: true }, false, 'centroCosto/openDialog'),
      closeDialog: () => set({ dialogOpen: false, selectedCentroCosto: null }, false, 'centroCosto/closeDialog'),
      clearError: () => set({ error: null }, false, 'centroCosto/clearError'),
    }),
    { name: 'CentroCostoStore' }
  )
);
